<template>
  <div id="app">
    <router-view v-if="!$store.state.app.waitingForConnection" />
    <offline v-else />
    <b-toast
      id="offline-toast"
      :title="$t('offline.title')"
      no-auto-hide
      no-close-button
      variant="warning"
      toaster="b-toaster-bottom-right"
      append="true"
    >
      {{ $t('offline.message') }}
    </b-toast>
  </div>
</template>

<script>
import Offline from '@/views/Offline';
import fp from '@fingerprintjs/fingerprintjs';
import { createUserManager } from './helpers/keycloak';

export default {
  name: 'App',
  components: {
    Offline,
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    online() {
      return this.$store.state.app.online;
    },
  },
  data() {
    return {
      userManager: null,
    };
  },
  methods: {
    checkConnection() {
      if (this.online === false) {
        this.$bvToast.show('offline-toast');
      } else {
        this.$bvToast.hide('offline-toast');
      }
    },
    async logout() {
      await this.$store.dispatch('auth/LOG_OUT', this.$kuzzle);
      this.$router.go('/');
    },
  },
  watch: {
    locale(newValue) {
      if (newValue) {
        localStorage.setItem('locale', this.locale);
      }
    },
    online: {
      immediate: false,
      handler() {
        this.checkConnection();
      },
    },
  },
  async mounted() {
    const fingerprint = await (await fp.load()).get();
    this.$store.commit('app/SET_VISITOR_ID', fingerprint.visitorId);

    this.userManager = createUserManager();
    this.userManager.events.addSilentRenewError(async (errorResponse) => {
      if (errorResponse.error !== 'invalid_grant') {
        return;
      }
      await this.$store.dispatch('auth/LOG_OUT', {
        kuzzle: this.$kuzzle,
        visitorId: fingerprint.visitorId,
        actionName: 'invalid session',
      });
      this.$router.push({ path: '/login?invalid=true' });
    });

    /**
     * APPLICATION BOOTSTRAP -- refactor this in a service if necessary.
     */
    this.$kuzzle.addListener('connected', () => {
      this.$store.commit('app/SET_ONLINE');
    });
    this.$kuzzle.addListener('reconnected', () => {
      this.$store.commit('app/SET_ONLINE');
    });
    this.$kuzzle.addListener('disconnected', () => {
      this.$store.commit('app/SET_OFFLINE');
    });
    await this.$kuzzle.addListener('tokenExpired', async () => {
      this.$store.dispatch('auth/LOG_OUT', {
        kuzzle: this.$kuzzle,
        visitorId: this.$store.state.app.visitorId,
        actionName: 'token expired',
      });
      await this.userManager.signoutRedirect();
    });
    const persistedLocale = localStorage.getItem('locale');
    if (persistedLocale) {
      this.$i18n.locale = persistedLocale;
    }
    await this.$kuzzle.connect();
    // Avoids showing the toast as soon as the app loads
    setTimeout(() => {
      this.checkConnection();
    }, 1000);
  },
};
</script>

<style lang="scss">
// CoreUI Icons Set
@import '~@coreui/icons/css/coreui-icons.min.css';
/* Import Font Awesome Icons Set */
$fa-font-path: '~font-awesome/fonts/';
@import '~font-awesome/scss/font-awesome.scss';
/* Import Bootstrap Vue Styles */
@import '~bootstrap-vue/dist/bootstrap-vue.css';
// Import Main styles for this application
@import 'assets/style';
@import '~@fortawesome/fontawesome-free/css/all.min.css';

#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $primary;
  // color: #2c3e50;
}

#nav {
  padding: 30px;
  a {
    font-weight: bold;
    // color: #2c3e50;
    color: $primary;
    &.router-link-exact-active {
      // color: #42b983;
      color: map-get($theme-colors, success);
    }
  }
}

.sidebar-nav {
  text-align: left;
}
</style>
